export const SITE = {
  name: "Warranty_Vault pro",
  tagline: "Never Miss a Warranty Again",
  supportEmail: "support@warrantyvaultforyou.com",
  altEmail: "warrantyvaultforyou@gmail.com",
  copyright: "© 2026 Warranty_Vault pro — All Rights Reserved",
};

export const SOCIALS = [
  { label: "X (Twitter)", handle: "@Warrantyvaultt", href: "https://x.com/Warrantyvaultt", icon: "x" },
  { label: "Instagram", handle: "@warrantyvaultforyou", href: "https://www.instagram.com/warrantyvaultforyou/", icon: "instagram" },
  { label: "Discord", handle: "Join the server", href: "https://discord.gg/bbE5nZ7YB9", icon: "discord" },
];

export const FEATURES = [
  {
    id: "storage",
    icon: "receipt",
    title: "Smart Receipt Storage",
    desc: "Upload receipts and documents instantly with secure cloud storage.",
  },
  {
    id: "tracking",
    icon: "bell",
    title: "Warranty Tracking",
    desc: "Automatic expiry reminders so you never lose coverage.",
  },
  {
    id: "history",
    icon: "history",
    title: "Product History",
    desc: "Keep a full record of every item you own — organized and accessible.",
  },
  {
    id: "secure",
    icon: "shield",
    title: "Secure & Private",
    desc: "Your data is protected with industry-standard security and encryption.",
  },
];

const A = "https://customer-assets-jt897jd0.emergentagent.net/job_github-site-polish/artifacts";
export const PREVIEWS = [
  { eyebrow: "Your Vault", title: "Store & Manage Receipts", sub: "Totals, active warranties and value at a glance", img: `${A}/f2l6j92f_1-dashboard.webp` },
  { eyebrow: "Organized", title: "Keep Every Receipt Organized", sub: "Search, tag and sort by category in seconds", img: `${A}/85w677a3_2-receipts.webp` },
  { eyebrow: "Notepad", title: "Save Important Notes", sub: "Private & shared notes on premium themed pages", img: `${A}/jb496rc4_3-notes.webp` },
  { eyebrow: "Community", title: "Connect With Other Users", sub: "Follow people, react and share wins", img: `${A}/hs9jmxtm_4-community.webp` },
  { eyebrow: "Private & Secure", title: "Your Records In One Secure Place", sub: "2FA, trusted devices and encrypted backups", img: `/previews/secure.webp` },
];

export const HERO_IMG = `${A}/f2l6j92f_1-dashboard.webp`;

export const INDIVIDUAL_PLANS = [
  {
    badge: "Default on signup", tier: "Free (Starter)", price: "Free", meta: "Included on signup",
    blurb: "Included when someone signs up.",
    features: ["Up to 10 receipts", "Basic OCR scanning", "Basic expiry reminders", "1 personal vault"],
  },
  {
    badge: "Most Popular", highlight: true, tier: "Pro Lifetime", price: "$19.99", meta: "1 account",
    planKey: "pro_lifetime", buyLink: "https://buy.stripe.com/aFadRagro9DQcLyeLV0480c",
    blurb: "Lifetime access for one individual account.",
    features: ["Unlimited receipts & warranties", "Priority OCR receipt scanning", "Export to PDF & CSV", "Smart expiry reminders", "Cloud-synced personal vault"],
  },
  {
    badge: "Household", tier: "Family Lifetime", price: "$39.99", meta: "up to 5 accounts",
    planKey: "family_lifetime", buyLink: "https://buy.stripe.com/dRmaEY6QObLYdPC6fp0480d",
    blurb: "Perfect for families managing multiple devices and purchases.",
    features: ["Everything in Pro Lifetime", "Share with up to 5 accounts", "Shared family vault", "Per-member expiry alerts", "Family activity overview"],
  },
  {
    badge: "Extended Family", tier: "Family Jumbo", price: "$79.99", meta: "5 accounts",
    planKey: "family_jumbo", buyLink: "https://buy.stripe.com/5kQ4gA8YW4jw4f2gU30480e",
    blurb: "For families who want both shared and private vaults.",
    features: ["Everything in Family Lifetime", "Every added account gets its OWN Pro Lifetime vault (private, not shared)", "5 full member vaults + priority OCR for everyone", "Family & individual analytics"],
  },
  {
    badge: "Signature", tier: "Signature Edition", price: "$999", meta: "up to 20 accounts",
    buyLink: "https://buy.stripe.com/6oU14o0sqaHU8vi8nx04808", planKey: "white_label_basic",
    blurb: "Brand-focused individual experience with your own look.",
    features: ["Your own logo & brand colours", "Fully branded dashboard", "Custom accents & banners", "Personalised onboarding look", "Up to 20 accounts"],
  },
  {
    badge: "Signature Jumbo", tier: "Signature Jumbo", price: "$1,999", meta: "20 accounts",
    buyLink: "https://buy.stripe.com/9B6aEYdfc5nA9zm33d04809", planKey: "white_label_basic_jumbo",
    blurb: "For larger groups needing branded, individual vaults.",
    features: ["Everything in Signature Edition", "Every added account gets its OWN Pro Lifetime vault", "20 full branded vaults + priority OCR everywhere"],
  },
];

export const BUSINESS_PLANS = [
  {
    badge: "Core Business", highlight: true, tier: "Business", price: "$999", meta: "up to 10 team accounts",
    planKey: "business_lifetime", buyLink: "https://buy.stripe.com/8x2eVe8YW03g6nagU304806",
    blurb: "For teams managing receipts, warranties, and tax-related documents.",
    features: ["Bulk receipt & warranty upload", "Tax tagging & custom categories", "Team accounts (up to 10)", "Business analytics dashboard", "Activity & audit logs"],
  },
  {
    badge: "Business Jumbo", tier: "Business Jumbo", price: "$1,999", meta: "10 accounts",
    buyLink: "https://buy.stripe.com/9B64gAa307vI26U7jt04807", planKey: "business_jumbo",
    blurb: "For teams needing both business tools and individual Pro vaults.",
    features: ["Everything in Business", "Every added account gets its OWN Pro Lifetime vault", "10 full team vaults", "Advanced bulk/automation tools"],
  },
  {
    badge: "Elite White-Label", tier: "Elite Edition", price: "$5,000", meta: "up to 20 accounts",
    buyLink: "https://buy.stripe.com/cNi28s5MK6rEdPC6fp0480a", planKey: "white_label_pro",
    blurb: "Full white-label solution for serious businesses.",
    features: ["Full white-label branding", "Custom domain", "Admin & business tools", "Team roles & permissions", "Up to 20 accounts + analytics"],
  },
  {
    badge: "Elite Jumbo", tier: "Elite Jumbo", price: "$10,000", meta: "20 accounts",
    buyLink: "https://buy.stripe.com/3cI14o8YW3fsh1O7jt0480b", planKey: "white_label_pro_jumbo",
    blurb: "For businesses needing maximum control and automation.",
    features: ["Everything in Elite Edition", "Every added account gets its OWN Pro Lifetime vault", "Custom domain & email templates", "Full admin/workflow automation"],
  },
];

export const INDIVIDUAL_COMPARISON = {
  headers: ["Plan", "Accounts", "Receipts & Warranties", "OCR & Reminders", "Branding / Extras"],
  rows: [
    ["Free (Starter)", "1", "Up to 10 receipts", "Basic OCR, basic expiry reminders", "1 personal vault"],
    ["Pro Lifetime", "1", "Unlimited", "Priority OCR, smart expiry reminders", "Cloud-synced personal vault, export tools"],
    ["Family Lifetime", "Up to 5", "Shared across family", "Per-member expiry alerts", "Shared family vault, activity overview"],
    ["Family Jumbo", "5", "Each member has own Pro vault", "Priority OCR for everyone", "Family & individual analytics"],
    ["Signature Edition", "Up to 20", "Unlimited", "Priority OCR", "Custom branding, dashboard, onboarding"],
    ["Signature Jumbo", "20", "Each account has own Pro vault", "Priority OCR everywhere", "20 full branded vaults"],
  ],
};

export const BUSINESS_COMPARISON = {
  headers: ["Plan", "Accounts", "Core Tools", "Vaults", "Branding / Extras"],
  rows: [
    ["Business", "Up to 10", "Bulk upload, tax tagging", "Shared team vault", "Analytics, audit logs"],
    ["Business Jumbo", "10", "Everything in Business + automation", "Each account own Pro vault", "10 full team vaults"],
    ["Elite Edition", "Up to 20", "Admin & business tools, roles", "White-label vaults", "Full white-label, custom domain"],
    ["Elite Jumbo", "20", "Full workflow automation", "Each account own Pro vault", "Custom domain & email templates"],
  ],
};

export const ALL_PLANS = [...INDIVIDUAL_PLANS, ...BUSINESS_PLANS];
export const PLAN_KEY_TO_NAME = ALL_PLANS.reduce((m, p) => {
  if (p.planKey) m[p.planKey] = p.tier;
  return m;
}, {});

export const planSlug = (p) => p.tier.toLowerCase().replace(/[^a-z0-9]+/g, "-");
export const planFromKey = (key) => (key && PLAN_KEY_TO_NAME[key] ? { name: PLAN_KEY_TO_NAME[key] } : null);
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.warrantyvaultforyou.app";
export const getPlanBySlug = (slug) => ALL_PLANS.find((p) => planSlug(p) === slug);
export const planSection = (p) => (BUSINESS_PLANS.includes(p) ? "business" : "individual");

export function planLabel(user) {
  if (!user) return "";
  if (user.plan_key && PLAN_KEY_TO_NAME[user.plan_key]) return PLAN_KEY_TO_NAME[user.plan_key];
  const t = user.plan_type;
  if (!t || t === "free") return "Free";
  const base = t.charAt(0).toUpperCase() + t.slice(1);
  return user.white_label_tier && user.white_label_tier !== "none" ? `${base} · ${user.white_label_tier}` : base;
}

export const FAQS = [
  { q: "How does Warranty_Vault pro store my data?", a: "Your receipts and documents are stored securely with industry-standard encryption." },
  { q: "Will I get reminders before a warranty expires?", a: "Yes — Warranty_Vault pro notifies you ahead of expiry so you have time to act." },
  { q: "Can I access Warranty_Vault pro from multiple devices?", a: "Yes — Warranty_Vault pro works on tablet and mobile." },
];

export const ABOUT = {
  lead: "Warranty_Vault pro was created to solve a simple problem: people lose receipts and forget warranty expiry dates.",
  mission: "Our mission is to help every household and business protect their purchases with simple, secure warranty tracking.",
};
